import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, LogOut, Package, MessageCircle, LogIn } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';

const enCartStorage = {
  getCart: () => {
    const cart = localStorage.getItem('cheat_cart_en');
    return cart ? JSON.parse(cart) : [];
  },
};

const HeaderEN = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const [cartCount, setCartCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const updateCartCount = () => {
      setCartCount(enCartStorage.getCart().length);
    };
    updateCartCount();
    window.addEventListener('storage', updateCartCount);
    const interval = setInterval(updateCartCount, 500);
    return () => {
      window.removeEventListener('storage', updateCartCount);
      clearInterval(interval);
    };
  }, []);

  const navLinks = [
    { to: '/en', label: 'Home' },
    { to: '/en/products', label: 'Products' },
    { to: '/en/feedbacks', label: 'Feedbacks' },
    ...(!isAuthenticated ? [{ to: '/en/login', label: 'Login' }] : []),
  ];

  return (
    <header className="bg-black border-b border-gray-800 sticky top-0 z-50 shadow-lg">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          <Link to="/en" className="flex items-center space-x-3 group">
            <div className="relative">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_07e3fa1c-accb-40af-b9f6-b18f873b4bec/artifacts/3s7e8l1h_logodas.png" 
                alt="Cloud Cheats Logo" 
                className="h-12 w-12 object-contain transition-transform group-hover:scale-110"
              />
            </div>
            <div className="hidden sm:block">
              <span className="text-white text-xl font-black tracking-wider" style={{ fontFamily: 'Orbitron, sans-serif' }}>CLOUD</span>
              <span className="text-red-600 text-xl ml-1 font-black tracking-wider" style={{ fontFamily: 'Orbitron, sans-serif' }}>CHEATS</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center space-x-2">
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to}>
                <Button
                  variant="ghost"
                  className={`text-gray-300 hover:text-white hover:bg-red-600/20 font-semibold transition-all ${
                    location.pathname === link.to ? 'bg-red-600/30 text-white border-b-2 border-red-600' : ''
                  }`}
                >
                  {link.label}
                </Button>
              </Link>
            ))}
          </nav>

          <div className="flex items-center space-x-2">
            {isAuthenticated && (
              <>
                <Button 
                  variant="ghost" 
                  onClick={logout}
                  className="hidden md:flex items-center text-gray-300 hover:text-white hover:bg-red-600/20 transition-all"
                >
                  <LogOut className="w-5 h-5 mr-2" />
                  Logout
                </Button>
              </>
            )}
            
            <Link to="/en/cart">
              <Button 
                variant="ghost" 
                className="relative text-gray-300 hover:text-white hover:bg-red-600/20 transition-all"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 bg-red-600 text-white h-5 w-5 flex items-center justify-center p-0 text-xs pulse-badge">
                    {cartCount}
                  </Badge>
                )}
              </Button>
            </Link>

            <Button
              variant="ghost"
              className="md:hidden text-gray-300 hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {mobileMenuOpen && (
          <nav className="md:hidden py-4 border-t border-gray-800 bg-black">
            <div className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                <Link key={link.to} to={link.to} onClick={() => setMobileMenuOpen(false)}>
                  <Button
                    variant="ghost"
                    className={`w-full justify-start text-gray-300 hover:text-white hover:bg-red-600/20 font-semibold ${
                      location.pathname === link.to ? 'bg-red-600/30 text-white' : ''
                    }`}
                  >
                    {link.label}
                  </Button>
                </Link>
              ))}
              {isAuthenticated && (
                <Button 
                  variant="ghost" 
                  onClick={() => { setMobileMenuOpen(false); logout(); }}
                  className="w-full justify-start text-gray-300 hover:text-white hover:bg-red-600/20 font-semibold"
                >
                  <LogOut className="w-5 h-5 mr-2" />
                  Logout
                </Button>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default HeaderEN;
